import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale } from '@/types/locale';

import { useGetAllCountryLabels } from './useGetAllCountryLabels';

/**
 * Normalize language code to handle 'fr', 'fr-FR', 'en', 'en-US', etc.
 */
function normalizeLanguage(language: string): 'fr' | 'en' {
  const normalized = language.toLowerCase().split('-')[0];
  return normalized === 'en' ? 'en' : 'fr';
}

export function getLocaleFromLanguage(language: string): string {
  const normalized = normalizeLanguage(language);
  return normalized === 'fr' ? Locale.FR : Locale.US;
}

export function getCountryName(
  label: { code: string; fr?: string | null; en?: string | null },
  language: string
): string {
  const normalized = normalizeLanguage(language);

  if (normalized === 'en') {
    return label.en ?? label.fr ?? label.code;
  }
  return label.fr ?? label.en ?? label.code;
}

export function useCountryNames(): Record<string, string> {
  const { i18n } = useTranslation();
  const { countryLabels } = useGetAllCountryLabels();

  return useMemo(() => {
    return countryLabels.reduce(
      (acc, label) => {
        acc[label.code] = getCountryName(label, i18n.language);
        return acc;
      },
      {} as Record<string, string>
    );
  }, [countryLabels, i18n.language]);
}
